import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bike, MapPin, Phone, Navigation, Radio, PackageCheck, XCircle, Store } from "lucide-react";
import { api, errMsg, fmt, fmtDate, DELIVERY, VEHICLE } from "../lib/api";
import MapView, { RouteInfo } from "../map/MapView";
import { getRoute } from "../map/routing";
import { Empty, Spinner, btnGhost, btnPrimary } from "../components/ui";
import ConfirmAction from "../components/ConfirmAction";

const TRACKABLE = ["picked_up", "in_transit"];

function useLocationSharing(delivery) {
  const [sharing, setSharing] = useState(false);
  const [pos, setPos] = useState(null);
  const watch = useRef(null);
  const last = useRef(0);
  const active = delivery && TRACKABLE.includes(delivery.status);
  const stop = () => { if (watch.current != null) navigator.geolocation.clearWatch(watch.current); watch.current = null; setSharing(false); };
  const start = () => {
    if (!navigator.geolocation) return toast.error("Geolocalização indisponível neste dispositivo");
    watch.current = navigator.geolocation.watchPosition((p) => {
      const loc = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
      setPos(loc);
      if (Date.now() - last.current > 10000) {
        last.current = Date.now();
        api.post(`/courier/deliveries/${delivery.id}/location`, loc).catch((e) => { toast.error(errMsg(e)); stop(); });
      }
    }, () => { toast.error("Autorização de localização recusada"); stop(); }, { enableHighAccuracy: true, maximumAge: 5000 });
    setSharing(true);
  };
  useEffect(() => { if (!active) stop(); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => stop, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { sharing, pos, start, stop, active };
}

function ActiveDelivery({ d, reload }) {
  const share = useLocationSharing(d);
  const [route, setRoute] = useState(null);
  const posKey = share.pos ? `${share.pos.lat.toFixed(4)},${share.pos.lng.toFixed(4)}` : "";
  useEffect(() => { if (share.pos && d.destination) getRoute(share.pos, d.destination).then(setRoute); }, [posKey, d.destination?.lat]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (action, body) => {
    try { await api.post(`/courier/deliveries/${d.id}/${action}`, body); toast.success("Estado atualizado"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const c = d.customer;
  const navUrl = d.destination && `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=;${d.destination.lat},${d.destination.lng}`;
  return (
    <div className="bo-card flex flex-col gap-3 p-4" data-testid={`courier-delivery-${d.order_number}`}>
      <div className="flex items-center justify-between"><p className="text-lg font-extrabold">{d.order_number}</p>
        <span className="rounded-full bg-bo-green/10 px-3 py-1 text-xs font-bold text-bo-green" data-testid="courier-delivery-status">{DELIVERY[d.status]}</span></div>
      <div className="text-sm">
        <p className="flex items-start gap-2"><Store className="mt-0.5 h-4 w-4 text-gray-400" />Recolha: {d.pickups.map((p) => `${p.name} (${p.location})`).join(" · ")}</p>
        <p className="mt-1 flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 text-gray-400" />{d.district}{d.landmark && ` · ${d.landmark}`}</p>
        <p className="mt-1 text-xs text-gray-500">{d.items_count} artigos{d.cod_amount > 0 && <> · <strong>Cobrar {fmt(d.cod_amount)}</strong> na entrega</>}</p>
      </div>
      {c && (
        <div className="rounded-xl bg-bo-bg p-3 text-sm" data-testid="courier-customer-info">
          <p className="font-semibold">{c.full_name}</p><p>{c.address_line}, {c.district}</p>
          {c.notes && <p className="text-xs text-gray-500">Notas: {c.notes}</p>}
          <a href={`tel:${c.phone}`} className="mt-1 inline-flex items-center gap-1 font-semibold text-bo-green" data-testid="courier-call-customer"><Phone className="h-4 w-4" />{c.phone}</a>
        </div>
      )}
      {d.destination && d.status !== "assigned" && (
        <>
          <MapView height={240} route={route} markers={[{ key: "me", pos: share.pos, icon: "courier", label: "Eu" }, { key: "d", pos: d.destination, icon: "dest", label: "Destino" }]} testid="courier-map" />
          <RouteInfo route={route} />
          <a href={navUrl} target="_blank" rel="noopener noreferrer" className={btnGhost} data-testid="courier-open-nav"><Navigation className="h-4 w-4" />Abrir navegação (OpenStreetMap)</a>
        </>
      )}
      {d.status !== "assigned" && !d.destination && <p className="text-xs text-amber-700">O cliente não marcou o ponto no mapa. Use a morada e as referências.</p>}
      {share.active && (
        <button onClick={share.sharing ? share.stop : share.start} className={share.sharing ? btnGhost : btnPrimary} data-testid="courier-share-location-btn">
          <Radio className="h-4 w-4" />{share.sharing ? "Parar partilha de localização" : "Partilhar localização com o cliente"}
        </button>
      )}
      {share.active && <p className="text-[11px] text-gray-500">A localização só é enviada durante esta entrega e termina automaticamente quando a concluir.</p>}
      <div className="grid grid-cols-2 gap-2">
        {d.status === "assigned" && <><button onClick={() => act("accept")} className={btnPrimary} data-testid="courier-accept-btn">Aceitar</button>
          <ConfirmAction testid="courier-refuse" title="Recusar esta entrega?" description="A encomenda volta para a administração para ser reatribuída." confirmLabel="Recusar" onConfirm={() => act("refuse")}
            trigger={<button className={btnGhost} data-testid="courier-refuse-btn">Recusar</button>} /></>}
        {d.status === "accepted" && <button onClick={() => act("pickup")} className={`${btnPrimary} col-span-2`} data-testid="courier-pickup-btn"><PackageCheck className="h-4 w-4" />Confirmar recolha</button>}
        {d.status === "picked_up" && <button onClick={() => act("start")} className={`${btnPrimary} col-span-2`} data-testid="courier-start-btn"><Bike className="h-4 w-4" />Iniciar entrega</button>}
        {d.status === "in_transit" && <div className="col-span-2"><ConfirmAction testid="courier-deliver" title="Confirmar entrega concluída" withReason="Código de entrega (4 dígitos) dado pelo cliente" pattern={/^\d{4}$/} description={d.cod_amount > 0 ? `Confirme que recebeu ${fmt(d.cod_amount)} do cliente.` : "Peça o código ao cliente no momento da entrega."} onConfirm={(code) => act("deliver", { code })}
          trigger={<button className={`${btnPrimary} w-full`} data-testid="courier-deliver-btn">Confirmar entrega concluída</button>} /></div>}
        {["accepted", "picked_up", "in_transit"].includes(d.status) && <div className="col-span-2"><ConfirmAction testid="courier-fail" title="Registar entrega falhada" withReason="Motivo (ex.: cliente ausente)" confirmLabel="Registar" onConfirm={(reason) => act("fail", { reason })}
          trigger={<button className={`${btnGhost} w-full text-red-600`} data-testid="courier-fail-btn"><XCircle className="h-4 w-4" />Entrega falhada</button>} /></div>}
      </div>
    </div>
  );
}

export default function CourierPanel() {
  const [me, setMe] = useState(null);
  const [list, setList] = useState(null);
  const load = () => {
    api.get("/courier/me").then(({ data }) => setMe(data)).catch(() => setMe(false));
    api.get("/courier/deliveries").then(({ data }) => setList(data)).catch(() => setList([]));
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  if (me === null || list === null) return <Spinner />;
  if (!me) return <Empty icon={Bike} title="Perfil de estafeta não encontrado" />;
  const toggle = async () => {
    try { await api.patch("/courier/me/availability", { available: !me.available }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const active = list.filter((d) => ["assigned", "accepted", "picked_up", "in_transit"].includes(d.status));
  const done = list.filter((d) => !active.includes(d));
  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4" data-testid="courier-panel">
      <div className="bo-card flex items-center gap-3 p-4">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-bo-green text-bo-yellow"><Bike className="h-6 w-6" /></span>
        <div className="flex-1"><p className="font-extrabold">{me.name}</p><p className="text-xs text-gray-500">OBÔGO Entregas · {VEHICLE[me.vehicle]} · {me.zone}</p></div>
        {me.status === "active" ? (
          <button onClick={toggle} className={`rounded-full px-4 py-2 text-sm font-bold ${me.available ? "bg-bo-green text-white" : "bg-gray-100 text-gray-600"}`} data-testid="courier-availability-toggle">
            {me.available ? "Disponível" : "Indisponível"}</button>
        ) : <span className="text-xs font-semibold text-amber-700" data-testid="courier-account-status">Conta {me.status}</span>}
      </div>
      <h2 className="font-bold">Entregas atribuídas ({active.length})</h2>
      {!active.length && <p className="bo-card p-6 text-center text-sm text-gray-500" data-testid="courier-no-deliveries">Sem entregas atribuídas de momento.</p>}
      {active.map((d) => <ActiveDelivery key={d.id} d={d} reload={load} />)}
      {done.length > 0 && <h2 className="font-bold">Histórico</h2>}
      {done.map((d) => (
        <div key={d.id} className="bo-card flex justify-between p-3 text-sm" data-testid={`courier-history-${d.order_number}`}>
          <span className="font-semibold">{d.order_number}</span><span>{DELIVERY[d.status]}</span><span className="text-xs text-gray-500">{fmtDate(d.updated_at)}</span>
        </div>
      ))}
    </div>
  );
}
